import { z } from "zod";
import {
  ColorOptionLabel,
  MiDetailFields,
  MiModal,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONFIGURABLE_COLORS,
  ENTITY_NAMES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { capitalize, createUUID } from "@miplatform/utils";

const initialOrderData = {
  name: "",
  shortName: "",
  color: "",
  tradeId: "",
  requiresApproval: false,
  refrenceId: createUUID(),
};

export function InspectionTemplateModal({
  open,
  title,
  buttonTitle,
  onClose,
  trades,
  loading,
  onSubmit = () => null,
}) {
  const inspectionTemplateFields = [
    {
      label: `${ENTITY_NAMES.INSPECTION_TEMPLATE} Name`,
      name: "name",
      autoFocus: true,
      beforeOnChange: capitalize,
      width: "100%",
      validationRule: z
        .string()
        .min(1, `${ENTITY_NAMES.INSPECTION_TEMPLATE} Name is required`),
      required: true,
    },
    {
      label: "Trade",
      name: "tradeId",
      fieldType: INPUT_TYPES.SELECT,
      options: trades,
      width: "100%",
      validationRule: z.string().min(1, "Trade Name is required"),
      required: true,
    },
    {
      label: "Abbr.",
      name: "shortName",
      beforeOnChange: (value) => value.toUpperCase(),
      width: "100%",
      validationRule: z.string().min(1, "Abbreviation is required"),
      required: true,
    },
    {
      label: "Color",
      name: "color",
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      fullWidth: true,
      width: "100%",
      validationRule: z.string().min(1, "Color is required"),
      required: true,
    },
  ];

  const inspectionTemplateEdit = useEdit(
    initialOrderData,
    inspectionTemplateFields
  );

  const handleModalClose = () => {
    onClose();
    inspectionTemplateEdit.reset();
  };

  const handleSubmit = () => {
    onSubmit(inspectionTemplateEdit.edits);
  };

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: buttonTitle,
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSubmit,
          disabled: loading || !inspectionTemplateEdit.isValid(),
        },
      ]}
      title={title}
      topRightClose={inspectionTemplateEdit.reset}
      loading={loading}
    >
      <MiDetailFields
        detailFields={inspectionTemplateFields}
        edit={inspectionTemplateEdit}
      />
    </MiModal>
  );
}
